import { defineStore } from 'pinia'
import {
  ConfigApi,
  ConfigHistoryApi,
  FetchPermissionsApi,
  CurrencyApi,
  CompanyApi
} from '@/api'
import Swal from 'sweetalert2'
import { getAccessTokenSilently } from '@/auth0'

// Time constants in milliseconds
const MINUTE = 60 * 1000
const DEFAULT_RECENT_LOGIN_TIME = 5 * MINUTE // 5 minutes (300,000 ms)

// Main Pinia Store
export const useTemplateStore = defineStore({
  id: 'template',
  state: () => ({
    // App vital details
    app: {
      name: 'Publinker',
      version: import.meta.env.VITE_PUBLINKER_VERSION || '',
      copyright: new Date().getFullYear()
    },

    // Default layout options
    layout: {
      header: true,
      sidebar: true,
      sideOverlay: true,
      footer: true
    },

    // Default template settings
    // Various of them are also set in each layout variation under layouts/variations/ folder
    settings: {
      colorTheme: 'amethyst', // 'amethyst', 'city', 'flat', 'modern', 'smooth'
      darkMode: false,
      darkModeSystem: false,
      sidebarLeft: true,
      sidebarMini: false,
      sidebarDark: false,
      sidebarVisibleDesktop: true,
      sidebarVisibleMobile: false,
      sideOverlayVisible: false,
      sideOverlayHoverable: false,
      pageOverlay: true,
      headerFixed: false,
      headerDark: false,
      headerSearch: false,
      headerLoader: false,
      pageLoader: false,
      rtlSupport: false,
      sideTransitions: true,
      mainContent: 'full' // 'boxed', ''narrow'
    },

    // Publinker
    selectedCompany: null,
    selectedSite: null,
    isLoading: [],
    sites: null,
    site: null,
    company: null,
    config: null,
    defaultPricingChannels: ['utm_source=', 'BFP REPORT - bfp_src='],
    isDemo: false,
    currency: {},
    permissions: [],
    configHistories: [],
    configDetail: {},

    // Login and session system
    recentLoginTimeWindow: DEFAULT_RECENT_LOGIN_TIME, // Time window to consider a login as recent

    // Connection state
    connection: {
      status: 'checking',
      isChecking: false,
      lastPingTime: 0,
      lastCheckTime: null,
      lastDowntime: null,
      reconnectionAttempts: 0,
      isOnline: navigator.onLine,
      history: [],
      wasOffline: false // To control reconnection notifications
    },

    isContextReady: false,
    permissionsNeedRefresh: false,
    siteDataLoaded: false
  }),
  getters: {
    isAdmin: state => {
      if (!state.permissions) return false
      return state.permissions.includes('overwatch:admin')
    },
    enabledModules: state => {
      if (!state.permissions || !Array.isArray(state.permissions)) return []

      let modules = state.permissions
        .filter(permission => permission.startsWith('module:'))
        .map(permission => permission.replace('module:', ''))
      return modules
    },
    siteList: state => {
      if (!state.permissions) return []
      return state.permissions
        .filter(p => p.indexOf('site:') > -1)
        .map(s => s.replace('site:', ''))
    },
    connectionDetails: state => {
      const responseTime = `${Math.round(state.connection.lastPingTime)}ms`

      if (!state.connection.isOnline) {
        return {
          message: 'Connection Lost',
          details:
            'Network status: Offline | Please check your internet connection | Local changes will be synced when back online',
          type: 'error'
        }
      }

      switch (state.connection.status) {
        case 'good':
          return {
            message: 'Excellent Connection',
            details: `Response time: ${responseTime} | Network stability: Optimal | Perfect for real-time operations`,
            type: 'success'
          }
        case 'medium':
          return {
            message: 'Moderate Connection',
            details: `Response time: ${responseTime} | Network stability: Fair | Suitable for most operations`,
            type: 'warning'
          }
        case 'poor':
          return {
            message: 'Poor Connection',
            details: `Response time: ${responseTime} | Network stability: Unstable | Some features may be affected`,
            type: 'error'
          }
        default:
          return {
            message: 'Checking Connection',
            details: 'Verifying network status...',
            type: 'secondary'
          }
      }
    }
  },
  persist: {
    paths: [
      'site',
      'settings.darkMode',
      'connection.history',
      'recentLoginTimeWindow'
    ]
  },
  actions: {
    // Sets the layout, useful for setting different layouts (under layouts/variations/)
    setLayout(payload) {
      this.layout.header = payload.header
      this.layout.sidebar = payload.sidebar
      this.layout.sideOverlay = payload.sideOverlay
      this.layout.footer = payload.footer
    },
    // Sets sidebar visibility (open, close, toggle)
    sidebar(payload) {
      if (window.innerWidth > 991) {
        if (payload.mode === 'open') {
          this.settings.sidebarVisibleDesktop = true
        } else if (payload.mode === 'close') {
          this.settings.sidebarVisibleDesktop = false
        } else if (payload.mode === 'toggle') {
          this.settings.sidebarVisibleDesktop =
            !this.settings.sidebarVisibleDesktop
        }
      } else {
        if (payload.mode === 'open') {
          this.settings.sidebarVisibleMobile = true
        } else if (payload.mode === 'close') {
          this.settings.sidebarVisibleMobile = false
        } else if (payload.mode === 'toggle') {
          this.settings.sidebarVisibleMobile =
            !this.settings.sidebarVisibleMobile
        }
      }
    },
    // Sets sidebar mini mode (on, off, toggle)
    sidebarMini(payload) {
      if (window.innerWidth > 991) {
        if (payload.mode === 'on') {
          this.settings.sidebarMini = true
        } else if (payload.mode === 'off') {
          this.settings.sidebarMini = false
        } else if (payload.mode === 'toggle') {
          this.settings.sidebarMini = !this.settings.sidebarMini
        }
      }
    },
    // Sets sidebar position (left, right, toggle)
    sidebarPosition(payload) {
      if (payload.mode === 'left') {
        this.settings.sidebarLeft = true
      } else if (payload.mode === 'right') {
        this.settings.sidebarLeft = false
      } else if (payload.mode === 'toggle') {
        this.settings.sidebarLeft = !this.settings.sidebarLeft
      }
    },
    // Sets sidebar style (dark, light, toggle)
    sidebarStyle(payload) {
      if (payload.mode === 'dark') {
        this.settings.sidebarDark = true
      } else if (payload.mode === 'light') {
        this.settings.sidebarDark = false
      } else if (payload.mode === 'toggle') {
        this.settings.sidebarDark = !this.settings.sidebarDark
      }
    },
    // Sets side overlay visibility (open, close, toggle)
    sideOverlay(payload) {
      if (payload.mode === 'open') {
        this.settings.sideOverlayVisible = true
      } else if (payload.mode === 'close') {
        this.settings.sideOverlayVisible = false
      } else if (payload.mode === 'toggle') {
        this.settings.sideOverlayVisible = !this.settings.sideOverlayVisible
      }
    },
    // Sets side overlay hover mode (on, off, toggle)
    sideOverlayHover(payload) {
      if (payload.mode === 'on') {
        this.settings.sideOverlayHoverable = true
      } else if (payload.mode === 'off') {
        this.settings.sideOverlayHoverable = false
      } else if (payload.mode === 'toggle') {
        this.settings.sideOverlayHoverable = !this.settings.sideOverlayHoverable
      }
    },
    // Sets page overlay visibility (on, off, toggle)
    pageOverlay(payload) {
      if (payload.mode === 'on') {
        this.settings.pageOverlay = true
      } else if (payload.mode === 'off') {
        this.settings.pageOverlay = false
      } else if (payload.mode === 'toggle') {
        this.settings.pageOverlay = !this.settings.pageOverlay
      }
    },
    // Sets header mode (fixed, static, toggle)
    header(payload) {
      if (payload.mode === 'fixed') {
        this.settings.headerFixed = true
      } else if (payload.mode === 'static') {
        this.settings.headerFixed = false
      } else if (payload.mode === 'toggle') {
        this.settings.headerFixed = !this.settings.headerFixed
      }
    },
    // Sets header style (dark, light, toggle)
    headerStyle(payload) {
      if (payload.mode === 'dark') {
        this.settings.headerDark = true
      } else if (payload.mode === 'light') {
        this.settings.headerDark = false
      } else if (payload.mode === 'toggle') {
        this.settings.headerDark = !this.settings.headerDark
      }
    },
    // Sets header search visibility (on, off, toggle)
    headerSearch(payload) {
      if (payload.mode === 'on') {
        this.settings.headerSearch = true
      } else if (payload.mode === 'off') {
        this.settings.headerSearch = false
      } else if (payload.mode === 'toggle') {
        this.settings.headerSearch = !this.settings.headerSearch
      }
    },
    // Sets header loader visibility (on, off, toggle)
    headerLoader(payload) {
      if (payload.mode === 'on') {
        this.settings.headerLoader = true
      } else if (payload.mode === 'off') {
        this.settings.headerLoader = false
      } else if (payload.mode === 'toggle') {
        this.settings.headerLoader = !this.settings.headerLoader
      }
    },
    // Sets page loader visibility (on, off, toggle)
    pageLoader(payload) {
      if (payload.mode === 'on') {
        this.settings.pageLoader = true
      } else if (payload.mode === 'off') {
        this.settings.pageLoader = false
      } else if (payload.mode === 'toggle') {
        this.settings.pageLoader = !this.settings.pageLoader
      }
    },
    // Sets main content mode (full, boxed, narrow)
    mainContent(payload) {
      if (payload.mode === 'full') {
        this.settings.mainContent = ''
      } else if (payload.mode === 'boxed') {
        this.settings.mainContent = 'boxed'
      } else if (payload.mode === 'narrow') {
        this.settings.mainContent = 'narrow'
      }
    },
    // Dark Mode
    darkMode(payload) {
      if (payload.mode === 'on') {
        this.settings.darkMode = true
      } else if (payload.mode === 'off') {
        this.settings.darkMode = false
      } else if (payload.mode === 'toggle') {
        this.settings.darkMode = !this.settings.darkMode
      }
    },
    // Dark Mode System based
    darkModeSystem(payload) {
      if (payload.mode === 'on') {
        this.settings.darkModeSystem = true

        // Check system preference
        if (
          window.matchMedia &&
          window.matchMedia('(prefers-color-scheme: dark)').matches
        ) {
          this.settings.darkMode = true
        } else {
          this.settings.darkMode = false
        }
      } else if (payload.mode === 'off') {
        this.settings.darkModeSystem = false
      }
    },
    // Sets active color theme
    setColorTheme(payload) {
      // Matches all classes which start with 'theme-'
      let regx = new RegExp('\\btheme-[^ ]*[ ]?\\b', 'g')

      // Set new theme
      this.settings.colorTheme = payload.theme || ''

      // Remove all classes which start with 'theme-' from body element
      document.body.className = document.body.className.replace(regx, '')

      // If theme is set, add the theme class to body element
      if (payload.theme) {
        document.body.classList.add('theme-' + payload.theme)
      }
    },
    // Sets side transitions
    setSideTransitions(payload) {
      this.settings.sideTransitions = payload.transitions
    },

    /**
     *
     * PUBLINKER BELOW
     *
     */
    setLoading(loading, key) {
      if (loading) {
        !this.isLoading.includes(key) && this.isLoading.push(key)
      } else {
        this.isLoading = this.isLoading.filter(k => k != key)
      }
    },
    async getAccessToken() {
      try {
        // Verify if permissions need to be updated
        if (this.permissionsNeedRefresh) {
          // Clear flag and token cache
          this.permissionsNeedRefresh = false
          localStorage.removeItem('publinker_auth_token_cache')
          localStorage.removeItem('publinker_auth_token_timestamp')
          console.debug('Token cache invalidated due to permissions update')
        }

        // Verify if there is a token in localStorage
        const cachedToken = localStorage.getItem('publinker_auth_token_cache')
        const cachedTokenTimestamp = localStorage.getItem(
          'publinker_auth_token_timestamp'
        )
        const currentTime = Date.now()

        // Define TTL to 30 minutes (1800000 ms)
        const TOKEN_TTL = 1800000

        // Verify if the cached token is still valid
        if (
          cachedToken &&
          cachedTokenTimestamp &&
          currentTime - parseInt(cachedTokenTimestamp) < TOKEN_TTL
        ) {
          console.debug('Using cached token from localStorage')
          return cachedToken
        }

        // Get a new token using the Auth0 singleton
        const token = await getAccessTokenSilently()

        // Store the token in cache
        localStorage.setItem('publinker_auth_token_cache', token)
        localStorage.setItem(
          'publinker_auth_token_timestamp',
          currentTime.toString()
        )

        return token
      } catch (error) {
        console.error('Error getting access token:', error)
        // Remove cache in case of error
        localStorage.removeItem('publinker_auth_token_cache')
        localStorage.removeItem('publinker_auth_token_timestamp')
        throw error
      }
    },

    async fetchCompanyBySite(site) {
      const loadingKey = 'company'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new CompanyApi(site).getEndpointUrl()
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal
      })
      const data = await response.json()
      this.setCompany(data)
      this.setLoading(false, loadingKey)
      return data
    },
    async fetchCompany(company_id) {
      const loadingKey = 'company'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new CompanyApi().getCompanyByIdEndpointUrl(company_id)
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal
      })
      const data = await response.json()
      this.setCompany(data)
      this.setLoading(false, loadingKey)
      return data
    },
    setCompany(payload) {
      this.company = payload.company
    },
    async fetchConfig(site) {
      if (!site || site === 'null' || site === '') return {}
      const loadingKey = 'config'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new ConfigApi(site).getEndpointUrl()
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal
      })
      const data = await response.json()
      this.setConfig(data)
      this.setLoading(false, loadingKey)
      return data
    },
    async fetchConfigIfNeeded() {
      if (!this.config) {
        await this.fetchConfig()
      }
    },
    setConfig(payload) {
      this.config = payload.config
    },
    async updateConfig(site, item) {
      const loadingKey = 'config'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new ConfigApi(site).getEndpointUrl()
      const response = await fetch(url, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal,
        body: JSON.stringify(item)
      })
      const data = await response.json()
      this.setConfig(data)
      this.setLoading(false, loadingKey)
      return data
    },
    setConfigPartial(key, payload) {
      this.config[key] = payload[key]
    },
    async updateConfigPartial(site, key, payload) {
      const loadingKey = 'config'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new ConfigApi(site).getPartialEndpointUrl()
      const response = await fetch(url, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal,
        body: JSON.stringify({ key, ...payload })
      })
      const data = await response.json()
      this.setConfigPartial(key, data)
      this.setLoading(false, loadingKey)
      return data
    },
    async fetchConfigHistory(site) {
      const loadingKey = 'configHistory'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new ConfigHistoryApi(site).getConfigHistoryUrl()
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal
      })
      const data = await response.json()
      this.setConfigHistories(data)
      this.setLoading(false, loadingKey)
      return data
    },
    setConfigHistories(configHistories) {
      this.configHistories = configHistories
    },
    async fetchConfigDetail(id) {
      const loadingKey = 'configHistory'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new ConfigHistoryApi(this.site).getEndpointUrl(
        encodeURIComponent(id)
      )
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal
      })
      const data = await response.json()
      this.setConfigHistoryDetail(data)
      this.setLoading(false, loadingKey)
    },
    setConfigHistoryDetail(configDetail) {
      this.configDetail = configDetail
    },
    async configDeployWrapper(config_new) {
      const loadingKey = 'configHistory'
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new ConfigApi(this.site).getDeployConfigWrapperEndpointUrl()
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal,
        body: JSON.stringify(config_new)
      })
      const data = await response.json()
      this.setLoading(false, loadingKey)
      return data
    },
    async fetchUserPermissions() {
      const loadingKey = 'permissions'
      this.setLoading(true, loadingKey)

      try {
        const token = await this.getAccessToken()
        const abortSignal = this.getAbortSignal()
        const url = new FetchPermissionsApi().getEndpointUrl()

        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Origin: window.location.origin
          },
          signal: abortSignal
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.setPermissions(data)
        return data.permissions
      } catch (error) {
        console.error('Error fetching user permissions:', error)
        Swal.fire({
          title: 'Erro ao Carregar Permissões',
          text: 'Não foi possível carregar as permissões necessárias. Por favor, tente novamente.',
          icon: 'error',
          showCancelButton: true,
          confirmButtonText: 'Recarregar Página',
          cancelButtonText: 'Fechar',
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#dc3545'
        }).then(result => {
          if (result.isConfirmed) {
            window.location.reload()
          }
        })
        throw error
      } finally {
        this.setLoading(false, loadingKey)
        this.pageLoader({ mode: 'off' })
      }
    },
    setPermissions(payload) {
      this.permissions = payload.permissions
    },
    async fetchCurrency(params) {
      const loadingKey = 'currency'
      this.setLoading(true, loadingKey)
      const token = await this.getAccessToken()
      const abortSignal = this.getAbortSignal()
      const url = new CurrencyApi().getEndpointUrl()

      const response = await fetch(url + '?' + new URLSearchParams(params), {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin
        },
        signal: abortSignal
      })
      const data = await response.json()
      this.setCurrency(data)
      this.setLoading(false, loadingKey)
      return data
    },
    setCurrency(payload) {
      this.currency = { ...this.currency, ...payload.currency }
    },
    getAbortSignal() {
      if (!this.abortController) {
        this.abortController = new AbortController()
      }
      return this.abortController.signal
    },
    abortRequests() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
    },
    showToast(message, type, config = null) {
      const defaultConfig = {
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        icon: type,
        title: message,
        customClass: {
          popup: 'sweet-toast-popup'
        }
      }

      const toastConfig = config
        ? { ...defaultConfig, ...config }
        : defaultConfig
      Swal.fire(toastConfig)
    },

    // Método para definir a janela de tempo para login recente
    setRecentLoginTimeWindow(minutes) {
      if (!minutes || minutes < 1) {
        console.warn('Invalid time window value, using default 5 minutes')
        this.recentLoginTimeWindow = DEFAULT_RECENT_LOGIN_TIME
        return
      }

      this.recentLoginTimeWindow = minutes * MINUTE
      console.debug(
        `Recent login time window set to ${minutes} minutes (${this.recentLoginTimeWindow} ms)`
      )
    },

    // Método para verificar se um timestamp é recente
    isTimestampRecent(timestamp) {
      if (!timestamp) return false

      const currentTime = Date.now()
      const elapsedTime = currentTime - parseInt(timestamp, 10)

      return elapsedTime < this.recentLoginTimeWindow
    },

    // Connection actions
    async checkLevel1Connection() {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 2000)

        await fetch('https://www.google.com/favicon.ico', {
          mode: 'no-cors',
          signal: controller.signal
        })

        clearTimeout(timeoutId)
        return true
      } catch (error) {
        console.error('Error checking connection quality:', error)
        return false
      }
    },

    async checkLevel2Connection() {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 2000)

        await fetch('https://cloudflare.com/favicon.ico', {
          mode: 'no-cors',
          signal: controller.signal
        })

        clearTimeout(timeoutId)
        return true
      } catch (error) {
        console.error('Error checking connection quality:', error)
        return false
      }
    },

    async checkLevel3Connection() {
      try {
        const start = performance.now()

        await fetch('https://jsonplaceholder.typicode.com/todos/1', {
          method: 'GET',
          mode: 'cors'
        })
        const end = performance.now()
        const pingTime = end - start

        return {
          success: true,
          pingTime
        }
      } catch (error) {
        console.error('Error checking connection quality:', error)
        return {
          success: false,
          pingTime: 0
        }
      }
    },

    async checkConnectionQuality() {
      if (!this.connection.isOnline) {
        this.updateConnectionStatus('poor', 0)
        return
      }

      this.connection.isChecking = true

      try {
        // Nível 1: Google
        const level1Result = await this.checkLevel1Connection()
        if (level1Result) {
          this.updateConnectionStatus('good', 0)
          return
        }

        // Nível 2: Cloudflare
        const level2Result = await this.checkLevel2Connection()
        if (level2Result) {
          // Registra instabilidade mas mantém status bom
          this.logInstability('Level 1 check failed, using fallback')
          this.updateConnectionStatus('good', 0)
          return
        }

        // Nível 3: API Ping
        const level3Result = await this.checkLevel3Connection()
        if (level3Result.success) {
          if (level3Result.pingTime < 200) {
            this.updateConnectionStatus('good', level3Result.pingTime)
          } else if (level3Result.pingTime < 500) {
            this.updateConnectionStatus('medium', level3Result.pingTime)
          } else {
            this.updateConnectionStatus('poor', level3Result.pingTime)
          }
          return
        }

        // Nível 4: Tudo falhou
        this.updateConnectionStatus('poor', 0)
      } catch (error) {
        console.error('Error checking connection quality:', error)
        this.updateConnectionStatus('poor', 0)
      } finally {
        setTimeout(() => {
          this.connection.isChecking = false
        }, 1000)
      }
    },

    updateConnectionStatus(status, pingTime) {
      this.connection.status = status
      this.connection.lastPingTime = pingTime
      this.connection.lastCheckTime = Date.now()

      // Registra no histórico
      this.connection.history.push({
        timestamp: Date.now(),
        status: status,
        pingTime: pingTime
      })

      // Mantém apenas os últimos 100 registros
      if (this.connection.history.length > 100) {
        this.connection.history.shift()
      }

      // Notifica apenas em caso de mudança crítica
      if (status === 'poor' && this.connection.wasOffline) {
        this.showConnectionToast('error', true)
      } else if (status !== 'poor' && this.connection.wasOffline) {
        this.showConnectionToast('success', true)
        this.connection.wasOffline = false
      }
    },

    logInstability(message) {
      console.warn(`Connection instability: ${message}`)
      this.connection.history.push({
        timestamp: Date.now(),
        type: 'instability',
        message: message
      })
    },

    updateOnlineStatus(isOnline) {
      this.connection.isOnline = isOnline
      if (isOnline) {
        this.checkConnectionQuality()
      } else {
        this.connection.status = 'poor'
        this.connection.lastDowntime = Date.now()
        // Notifica perda de conexão
        this.showConnectionToast('error', true)
      }
    },

    // Função específica para toasts de conexão
    showConnectionToast(type, forceShow = false) {
      if (!forceShow) return // Não mostra toast a menos que seja forçado

      const details = this.connectionDetails
      const config = {
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        icon: type,
        title: details.message,
        html: `
          <div class="connection-toast">
            <p class="connection-details">${details.details}</p>
          </div>
        `,
        customClass: {
          popup: 'sweet-toast-popup connection-toast-popup',
          title: 'connection-toast-title',
          htmlContainer: 'connection-toast-content',
          actions: 'toast-actions'
        }
      }

      this.showToast(details.message, type, config)
    },

    formatElapsedTime(startTime) {
      if (!startTime) return '0'
      const elapsed = Date.now() - startTime
      if (elapsed < 60000) return `${Math.floor(elapsed / 1000)} seconds`
      return `${Math.floor(elapsed / 60000)} minutes`
    },

    async loadContext(siteParam = null) {
      this.isContextReady = false

      if (!this.site && siteParam) {
        this.site = siteParam
      }

      await this.fetchConfig(siteParam || this.site)

      // Usar o novo método fetchUserPermissions
      await this.fetchUserPermissions()

      if (!this.company) {
        await this.fetchCompanyBySite(this.site)
      }
      this.isContextReady = true
    },

    clearTokenCache() {
      // Function to manually invalidate the token cache
      localStorage.removeItem('publinker_auth_token_cache')
      localStorage.removeItem('publinker_auth_token_timestamp')
      console.log('Token cache invalidated')
    }
  }
})
